import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getCompany,
  listSettings,
  updateCompany,
} from "./settingsService";

const DEFAULTS = {
  company_name: "",
  short_name: "",
  policy_prefix: "",
  policy_sequence_length: 6,
  next_policy_sequence: 1,
  email: "",
  phone: "",
  website: "",
  primary_color: "#660030",
  secondary_color: "#003366",
  accent_color: "#8A1748",
  address: "",
  default_currency_code: "USD",
};

function ColourField({
  label,
  name,
  value,
  onChange,
}) {
  return (
    <label className="colour-setting">
      <span>{label}</span>

      <div className="colour-setting__control">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(event) =>
            onChange(name, event.target.value.toUpperCase())
          }
          aria-label={`${label} colour picker`}
        />

        <div>
          <strong
            style={{
              background: value || "#000000",
            }}
          ></strong>
          <input
            type="text"
            value={value || ""}
            maxLength={7}
            placeholder="#000000"
            onChange={(event) =>
              onChange(name, event.target.value.toUpperCase())
            }
          />
        </div>
      </div>

      <small>
        Click the colour square to choose visually.
      </small>
    </label>
  );
}

export default function CompanySetup() {
  const [data, setData] = useState(DEFAULTS);
  const [logo, setLogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const [result, currencyList] = await Promise.all([
          getCompany(),
          listSettings("currencies"),
        ]);

        if (active) {
          setCurrencies(currencyList);
          setData({
            ...DEFAULTS,
            ...result,
          });
        }
      } catch (error) {
        toast.error("Could not load company setup.");
      }
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  const updateField = (name, value) => {
    setData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const save = async (event) => {
    event.preventDefault();

    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (
        value !== null &&
        value !== undefined &&
        key !== "logo_url" &&
        key !== "preview_policy_number"
      ) {
        formData.append(key, value);
      }
    });

    if (logo) {
      formData.append("logo", logo);
    }

    try {
      setSaving(true);
      const result = await updateCompany(formData);

      setData({
        ...DEFAULTS,
        ...result,
      });

      window.dispatchEvent(
        new Event("netri-branding-updated")
      );
      toast.success("Company setup saved.");
    } catch (error) {
      toast.error("Could not save company setup.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-panel">
      <div className="settings-panel__head">
        <div>
          <h2>Company setup</h2>
          <p>
            Client identity, branding and policy-number
            configuration used after login.
          </p>
        </div>
      </div>

      <form
        className="settings-form-grid company-form"
        onSubmit={save}
      >
        <label>
          <span>Company name</span>
          <input
            value={data.company_name}
            onChange={(event) =>
              updateField(
                "company_name",
                event.target.value.toUpperCase()
              )
            }
          />
        </label>

        <label>
          <span>Short name</span>
          <input
            value={data.short_name || ""}
            onChange={(event) =>
              updateField("short_name", event.target.value.toUpperCase())
            }
          />
        </label>

        <label>
          <span>Policy prefix</span>
          <input
            value={data.policy_prefix || ""}
            onChange={(event) =>
              updateField(
                "policy_prefix",
                event.target.value.toUpperCase()
              )
            }
          />
        </label>

        <label>
          <span>Sequence length</span>
          <input
            type="number"
            min="1"
            max="20"
            value={data.policy_sequence_length}
            onChange={(event) =>
              updateField(
                "policy_sequence_length",
                event.target.value
              )
            }
          />
        </label>

        <label>
          <span>Next sequence</span>
          <input
            type="number"
            min="1"
            value={data.next_policy_sequence}
            readOnly
          />
          <small>
            Automatically synchronised from the highest existing
            policy number.
          </small>
        </label>

        <div className="policy-preview">
          <span>Next policy preview</span>
          <strong>
            {data.preview_policy_number || "—"}
          </strong>
        </div>

        <label>
          <span>Default / base currency</span>
          <select
            value={data.default_currency_code || "USD"}
            onChange={(event) =>
              updateField(
                "default_currency_code",
                event.target.value
              )
            }
          >
            {currencies
              .filter((currency) => currency.is_active)
              .map((currency) => (
                <option
                  key={currency.id}
                  value={currency.currency_code}
                >
                  {currency.currency_code} — {currency.name}
                </option>
              ))}
          </select>
          <small>
            New proposals use this currency. Existing policies keep
            their original contract currency. Current premium setup
            is converted when the base currency changes.
          </small>
        </label>

        <label>
          <span>Email</span>
          <input
            type="email"
            value={data.email || ""}
            onChange={(event) =>
              updateField("email", event.target.value)
            }
          />
        </label>

        <label>
          <span>Phone</span>
          <input
            value={data.phone || ""}
            onChange={(event) =>
              updateField("phone", event.target.value)
            }
          />
        </label>

        <label>
          <span>Website</span>
          <input
            type="url"
            value={data.website || ""}
            onChange={(event) =>
              updateField("website", event.target.value)
            }
          />
        </label>

        <ColourField
          label="Primary colour"
          name="primary_color"
          value={data.primary_color}
          onChange={updateField}
        />

        <ColourField
          label="Secondary colour"
          name="secondary_color"
          value={data.secondary_color}
          onChange={updateField}
        />

        <ColourField
          label="Accent colour"
          name="accent_color"
          value={data.accent_color}
          onChange={updateField}
        />

        <label className="settings-wide-field">
          <span>Address</span>
          <textarea
            rows="3"
            value={data.address || ""}
            onChange={(event) =>
              updateField("address", event.target.value.toUpperCase())
            }
          />
        </label>

        <label className="settings-logo-field">
          <span>Company logo</span>

          <div className="settings-logo-upload">
            {data.logo_url ? (
              <img
                src={data.logo_url}
                alt="Current company logo"
              />
            ) : (
              <div className="settings-logo-placeholder">
                <i className="bi bi-building"></i>
              </div>
            )}

            <div>
              <input
                type="file"
                accept="image/*"
                onChange={(event) =>
                  setLogo(event.target.files?.[0] || null)
                }
              />
              <small>
                Used in the sidebar, navbar and documents
                after login.
              </small>
            </div>
          </div>
        </label>

        <div className="settings-sticky-actions">
          <div>
            <strong>Company setup</strong>
            <span>
              Save changes before leaving this section.
            </span>
          </div>

          <button type="submit" disabled={saving}>
            <i className="bi bi-check2-circle"></i>
            {saving ? "Saving..." : "Save company setup"}
          </button>
        </div>
      </form>
    </section>
  );
}
